/**
 * What the lodge has to rent out on any single night. Both counts come from
 * the real room list and the real fleet, so adding a room or a boat there
 * updates every availability number on the site.
 */
import { ROOMS } from './rooms'
import { FLEET } from './fleet'

export const ROOM_COUNT = ROOMS.length
export const BOAT_COUNT = FLEET.length


export type AvailabilityRow = {
  id: string
  arrival_date: string | null
  departure_date: string | null
  party_size: number | null
  notes: string | null
}

const DAY = 86_400_000

function parseDay(iso: string | null): number | null {
  if (!iso) return null
  const t = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`)
  return Number.isFinite(t) ? t : null
}

function boatsFromNotes(notes: string | null): number {
  const m = notes?.match(/boats:\s*(\d+)/i)
  return m ? Math.max(0, Number(m[1])) : 0
}

/** Nights a booking occupies: arrival through the night before departure. */
export function occupiedNights(row: AvailabilityRow): string[] {
  const start = parseDay(row.arrival_date)
  if (start == null) return []
  const end = parseDay(row.departure_date)
  const last = end != null && end > start ? end - DAY : start
  const days: string[] = []
  for (let t = start; t <= last && days.length < 120; t += DAY) {
    days.push(new Date(t).toISOString().slice(0, 10))
  }
  return days
}

export type NightAvailability = {
  roomsBooked: number
  boatsBooked: number
  roomsFree: number
  boatsFree: number
}

export const EMPTY_NIGHT: NightAvailability = {
  roomsBooked: 0,
  boatsBooked: 0,
  roomsFree: ROOM_COUNT,
  boatsFree: BOAT_COUNT,
}

/**
 * Rooms and boats still open for every night touched by these bookings.
 * One guest occupies one private room, so rooms booked = anglers on the books.
 */
export function availabilityByNight(rows: AvailabilityRow[]): Map<string, NightAvailability> {
  const map = new Map<string, NightAvailability>()
  for (const row of rows) {
    const rooms = Math.max(0, row.party_size ?? 0)
    const boats = boatsFromNotes(row.notes)
    for (const night of occupiedNights(row)) {
      const entry = map.get(night) ?? { roomsBooked: 0, boatsBooked: 0, roomsFree: 0, boatsFree: 0 }
      entry.roomsBooked += rooms
      entry.boatsBooked += boats
      map.set(night, entry)
    }
  }
  for (const entry of map.values()) {
    entry.roomsFree = Math.max(0, ROOM_COUNT - entry.roomsBooked)
    entry.boatsFree = Math.max(0, BOAT_COUNT - entry.boatsBooked)
  }
  return map
}
